import React, { FunctionComponent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'

import { request } from '../../helpers/apiHelper'
import { selectCurrentUserBadges } from '../../reducers'
import { UUID_REGEX } from '../admin/adminForm'
import { currentUserOrganizationQuery } from './queries'

// Where the CallTools webhook (blinker Api::V3::Webhooks::CalltoolsController)
// sends the agent when it cannot open a deal. Public on purpose: the agent may
// not be logged in yet, and a login detour would hide the reason.
//
// `reason` is rendered from this allow-list only, never echoed, so arbitrary
// query text cannot end up on the page. Keep the keys in step with the
// controller's FAILURE_STATUSES.
interface ReasonCopy {
  title: string
  body: string
  steps: string[]
  // Admins can fix the agent link themselves; everything else goes to support.
  adminFixable: boolean
}

const REASONS: { [reason: string]: ReasonCopy } = {
  auth_failed: {
    title: "Your dialer integration isn't set up correctly",
    body: 'Your dialer could not sign in to Blinker for your organization.',
    steps: [
      'Contact Blinker support and share the reference below.',
      'Once support confirms the fix, start the call action again from your dialer.',
    ],
    adminFixable: false,
  },
  agent_id_missing: {
    title: "Your dialer didn't send your agent ID",
    body: 'The call action that opened this page is not passing your agent ID.',
    steps: [
      'Ask your admin to check the webhook in your dialer includes the agent ID field.',
      'Start the call action again from your dialer.',
    ],
    adminFixable: false,
  },
  agent_not_linked: {
    title: "Your agent ID isn't linked to MissionControl",
    body: 'The agent ID from your dialer has to be saved on your MissionControl user before calls can open here.',
    steps: [
      'Ask your MissionControl admin to add your agent ID to your user.',
      'Start the call action again from your dialer.',
    ],
    adminFixable: true,
  },
  invalid_contact: {
    title: "This customer's details are incomplete",
    body: 'The details your dialer sent for this customer did not pass validation, for example a missing or non-US address.',
    steps: [
      "Update the customer's address and vehicle details in your dialer.",
      'Start the call action again from your dialer.',
    ],
    adminFixable: false,
  },
  no_destination: {
    title: 'No deal could be created from this call',
    body: 'Your dialer did not send enough vehicle details to open a deal.',
    steps: [
      "Check the customer's vehicle details in your dialer.",
      'Start the call action again from your dialer.',
    ],
    adminFixable: false,
  },
  sync_failed: {
    title: "We couldn't load this customer",
    body: 'Something went wrong on our side while bringing this call into MissionControl.',
    steps: [
      'Start the call action again from your dialer.',
      'If it keeps happening, contact Blinker support and share the reference below.',
    ],
    adminFixable: false,
  },
}

const FALLBACK: ReasonCopy = {
  title: 'Something went wrong opening this call',
  body: "We couldn't open this call in MissionControl.",
  steps: [
    'Start the call action again from your dialer.',
    'If it keeps happening, contact Blinker support and share the reference below.',
  ],
  adminFixable: false,
}

// Whether the viewer may save the CallTools agent id on their own admin
// record. Mirrors blinker's AdminAbility#update for a self-edit that does not
// touch roles: `edit_user`, or an org admin (`admin` without `superuser`). The
// /admins/:id/edit route additionally needs ADMIN or SUPERUSER. Keep in step
// with app/models/admin_ability.rb so the button never leads to a failed save.
const canEditOwnAgentId = (badges: string[]) => {
  const has = (badge: string) => badges.includes(badge)
  return (
    (has('ADMIN') && (!has('SUPERUSER') || has('EDIT_USER'))) ||
    (has('SUPERUSER') && has('EDIT_USER'))
  )
}

// blinker adds the unlinked CallTools agent id for `agent_not_linked`. Shown
// only when it is a UUID, so nothing arbitrary from the URL reaches the page.

interface CopyableValueProps {
  label: string
  value: string
}

const CopyableValue: FunctionComponent<CopyableValueProps> = ({
  label,
  value,
}) => {
  const [copied, setCopied] = useState(false)

  // The clipboard API rejects inside an unfocused iframe (as CallTools may
  // embed this page) or without permission; the value stays selectable, so a
  // failed copy is not worth an error state.
  const copyValue = async () => {
    if (!navigator.clipboard?.writeText) return
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (_error) {
      setCopied(false)
    }
  }

  return (
    <div className="calltools-error__reference">
      <div className="calltools-error__reference-text">
        <span className="calltools-error__reference-label">{label}</span>
        <code>{value}</code>
      </div>
      <button
        type="button"
        className="button button--secondary-round calltools-error__copy"
        onClick={copyValue}
        aria-label={`Copy ${label}`}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

// blinker lists the organizations it searched for an unlinked agent: the ones
// sharing the dialer's API key. Digits only, so nothing arbitrary from the URL
// is ever used.
const parseOrganizations = (raw: string | null) =>
  (raw || '').split(',').filter((id) => /^\d+$/.test(id))

type Scope = 'checking' | 'inside' | 'outside' | 'unknown'

// Whether the signed-in user belongs to one of the organizations searched. An
// agent id saved on a user outside them can never link, so the page says so
// instead of offering a save that loops. Anything short of a clear answer is
// 'unknown' and the page stays as it was.
const useOrganizationScope = (
  searched: string[],
  userId: number | string | undefined
) => {
  const wanted = searched.join(',')
  const [scope, setScope] = useState<Scope>(
    wanted && userId ? 'checking' : 'unknown'
  )
  const [hasOrganization, setHasOrganization] = useState(true)

  useEffect(() => {
    if (!wanted || !userId) {
      setScope('unknown')
      return undefined
    }

    let cancelled = false
    setScope('checking')
    request(currentUserOrganizationQuery, {})
      .then((response) => {
        if (cancelled) return
        const user = response.data?.data?.currentUser
        if (response.data?.errors || !user) {
          setScope('unknown')
          return
        }
        const organizationId = user.organizationId
        setHasOrganization(Boolean(organizationId))
        setScope(
          organizationId && wanted.split(',').includes(String(organizationId))
            ? 'inside'
            : 'outside'
        )
      })
      // A failed lookup leaves the page as it was.
      .catch(() => !cancelled && setScope('unknown'))

    return () => {
      cancelled = true
    }
  }, [wanted, userId])

  return { scope, hasOrganization }
}

export const CalltoolsError: FunctionComponent = () => {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const reference = params.get('ref')
  const reason = params.get('reason') || ''
  const copy = REASONS[reason] || FALLBACK

  const rawAgentId = params.get('agent') || ''
  const agentId =
    copy.adminFixable && UUID_REGEX.test(rawAgentId) ? rawAgentId : null
  const searchedOrganizations =
    reason === 'agent_not_linked'
      ? parseOrganizations(params.get('organizations'))
      : []

  const badges: string[] = useSelector(selectCurrentUserBadges) || []
  // CallTools opens this page in the agent's own browser, so the logged-in
  // user is usually the agent whose id is missing. Not always (a shared
  // browser, an admin opening the agent's link), so the button names whose
  // record it opens, the form only prefills an empty field, and admins also
  // get the list to pick someone else.
  const sessionUser = useSelector((state: any) => state.session?.user) || {}
  const currentUserId: number | string | undefined = sessionUser.id
  const currentUserName = [sessionUser.first_name, sessionUser.last_name]
    .filter(Boolean)
    .join(' ')
  const isAdmin = badges.includes('ADMIN') || badges.includes('SUPERUSER')
  const { scope, hasOrganization } = useOrganizationScope(
    searchedOrganizations,
    currentUserId
  )
  const outsideOrganization = scope === 'outside'
  const signedInAs = currentUserName
    ? `You are signed in to MissionControl as ${currentUserName}, who`
    : 'The MissionControl user you are signed in as'
  // Held back while the check runs and when it finds the save cannot work, so
  // the button never shows and then disappears.
  const canSelfFix =
    copy.adminFixable &&
    !!currentUserId &&
    canEditOwnAgentId(badges) &&
    scope !== 'checking' &&
    !outsideOrganization
  const selfFixPath = agentId
    ? `/admins/${currentUserId}/edit?calltoolsAgentId=${agentId}`
    : `/admins/${currentUserId}/edit`

  // Whoever can fix it is shown the fix; everyone else is pointed at the id
  // to hand to their admin.
  let steps = copy.steps
  if (outsideOrganization) {
    steps = [
      'Sign in to MissionControl as a user in the organization your dialer is connected to, or ask your admin to add the agent ID to a user there.',
      ...copy.steps.slice(1),
    ]
  } else if (canSelfFix) {
    steps = [
      'Open your admin user and save your agent ID on it.',
      ...copy.steps.slice(1),
    ]
  } else if (agentId) {
    steps = [
      'Send your MissionControl admin the agent ID below and ask them to add it to your user.',
      ...copy.steps.slice(1),
    ]
  }

  return (
    <div className="calltools-error">
      <div className="calltools-error__card" role="alert">
        <div className="calltools-error__icon" aria-hidden="true">
          !
        </div>
        <h2 className="calltools-error__title">{copy.title}</h2>
        <p className="calltools-error__body">{copy.body}</p>
        {outsideOrganization && (
          <p className="calltools-error__body" data-testid="organization-note">
            <strong>
              {signedInAs}{' '}
              {hasOrganization
                ? 'belongs to a different organization than the one your dialer is connected to.'
                : 'is not in an organization, but your dialer is connected to one.'}
            </strong>{' '}
            Saving the agent ID on this user will not link it.
          </p>
        )}

        <div className="calltools-error__steps">
          <h3 className="calltools-error__steps-title">What to do</h3>
          <ol>
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>

        {agentId && <CopyableValue label="Your agent ID" value={agentId} />}
        {reference && <CopyableValue label="Reference" value={reference} />}

        <div className="calltools-error__actions">
          {canSelfFix && (
            <Link className="button button--primary-round" to={selfFixPath}>
              {currentUserName
                ? `Add agent ID to my user (${currentUserName})`
                : 'Add agent ID to my user'}
            </Link>
          )}
          {copy.adminFixable && isAdmin && (
            <Link className="button button--secondary-round" to="/admins">
              Choose another user
            </Link>
          )}
          <Link className="button button--secondary-round" to="/">
            Back to MissionControl
          </Link>
        </div>
      </div>
    </div>
  )
}

export default CalltoolsError
